import * as React from 'react';
import parse from 'html-react-parser';
import { stripHtml } from "string-strip-html";

import {
  REVISE_MATCHED,
  REVISE_UNMATCHED,
  CORRECT_MATCHED,
  OVERRIDE,
  CONTINUE,
  INCORRECT_CONTINUE,
  INSTRUCTIONS,
  GET_QUESTION_INSTRUCTIONS,
  DEFAULT_WITH_CUES,
  DEFAULT,
  DEFAULT_FILL_IN_BLANK,
  SMALL_ICON_BASE_SRC
} from '../../utils/constants';
import { ENGLISH, rightToLeftLanguages } from '../../utils/languageList';
import C from '../../../Connect/constants';
import { ReadAloudButton } from '../readAloud/readAloudButton';
import { useReadAloud } from '../readAloud/readAloudContext';
import { useReadAloudSection } from '../readAloud/readAloudSection';
import { UNSPOKEN_CLASS } from '../../libs/speech/speechText';

const informationIcon = `${SMALL_ICON_BASE_SRC}/information.svg`
const reviseIcon = `${SMALL_ICON_BASE_SRC}/revise.svg`
const successIcon = `${SMALL_ICON_BASE_SRC}/check-circle.svg`
const multipleChoiceIcon = `${SMALL_ICON_BASE_SRC}/multiple-choice.svg`
const arrowIcon = `${SMALL_ICON_BASE_SRC}/continue.svg`
const brownArrow = "https://assets.quill.org/images/icons/continue-brown.svg"
const CONNECT_FILL_IN_BLANKS = "connect_fill_in_blanks"

function getIconClassName(feedbackType: string): string {
  let returnVal;
  switch (feedbackType) {
    case REVISE_UNMATCHED:
    case REVISE_MATCHED:
      returnVal = 'revise';
      break;
    case CORRECT_MATCHED:
      returnVal = 'success';
      break;
    case OVERRIDE:
      returnVal = 'multiple';
      break;
    case CONTINUE:
      returnVal = 'continue';
      break;
    case INSTRUCTIONS:
    case GET_QUESTION_INSTRUCTIONS:
    case DEFAULT_WITH_CUES:
    case DEFAULT:
      returnVal = 'info';
      break;
    default:
      returnVal = 'info';
  }
  return returnVal;
}

function getFeedbackIcon(feedbackType: string): string {
  let returnVal;
  switch (feedbackType) {
    case REVISE_UNMATCHED:
    case REVISE_MATCHED:
      returnVal = reviseIcon;
      break;
    case CORRECT_MATCHED:
      returnVal = successIcon;
      break;
    case OVERRIDE:
      returnVal = multipleChoiceIcon;
      break;
    case INCORRECT_CONTINUE:
      returnVal = brownArrow;
      break;
    case CONTINUE:
      returnVal = arrowIcon;
      break;
    case INSTRUCTIONS:
    case GET_QUESTION_INSTRUCTIONS:
    case DEFAULT_WITH_CUES:
    case DEFAULT:
    default:
      returnVal = informationIcon;
  }
  return returnVal;
}

function getFeedbackState(feedbackType: string): string {
  switch (feedbackType) {
    case REVISE_UNMATCHED:
    case REVISE_MATCHED:
    case INCORRECT_CONTINUE:
      return "revise"
    case CORRECT_MATCHED:
      return "success"
    default:
      return "default"
  }
}

function getCSSClasses(feedbackType: string): string {
  return "student-feedback-container " + getFeedbackState(feedbackType)
}

interface FeedbackProps {
  correctResponse?: string,
  feedbackType: any,
  feedbackText?: string,
  feedbackHtml?: string,
  language?: string,
  latestAttempt?: any,
  question?: any,
  showTranslation?: boolean,
  translate?: (input: string) => string,
  translatedFeedbackText?: string,
  languageLocale?: string;
}

function translatedCMSResponseFeedback(latestAttempt, question) {
  const { response } = latestAttempt
  const { translation } = question
  const cmsResponses = translation?.cms_responses
  const id = response?.id || response?.parent_id
  const translatedResponse = cmsResponses && id ? cmsResponses[`cms_responses.${id}`] : null
  return translatedResponse ? parse(translatedResponse) : null
}

function translatedIncorrectContinueFeedback(translate, latestAttempt, correctResponse) {
  const firstPhrase = translate('feedback^Good try!')
  const secondPhrase = translate('feedback^Compare your response to the strong response, and then go on to the next question.')
  const thirdPhrase = translate('feedback^Your response:')
  const fourthPhrase = translate('feedback^A strong response:')
  return (
    <div className="incorrect-continue">
      <p><b>{firstPhrase}</b> {secondPhrase}</p>
      <dl className="response-comparison">
        <dt>{thirdPhrase}</dt>
        <dd>{parse(latestAttempt)}</dd>
        <dt>{fourthPhrase}</dt>
        <dd>{parse(correctResponse)}</dd>
      </dl>
    </div>
  )
}

function translatedReviseMatchedFeedback(question, latestAttempt, translate) {
  const { response } = latestAttempt
  if (response?.isIncorrectSequence && question?.translation?.incorrectSequences) {
    const { uid } = response
    const { translation } = question
    const { incorrectSequences } = translation
    const key = `incorrectSequences.${uid}`
    return incorrectSequences[key] ? parse(incorrectSequences[key]) : null
  }
  if (response?.isFocusPoint && question?.translation?.focusPoints) {
    const { uid } = response
    const { translation } = question
    const { focusPoints } = translation
    const key = `focusPoints.${uid}`
    return focusPoints[key] ? parse(focusPoints[key]) : null
  }
  if(response?.author) {
    const { author, feedback } = response
    if(author === "Quotation Mark Hint") {
      return(
        <span>
          <br />
          <p>{translate('feedback^It looks like you might have used two apostrophes to make a quotation mark.')}</p>
          <p>{translate('feedback^Instead of hitting the apostrophe key twice to make a quotation mark, hold down the shift key and hit the apostrophe key once.')}</p>
        </span>
      )
    }
    if(C.ERROR_AUTHORS.includes(author)) {
      // in a few instances, the feedback here comes through as an HTML string, so we need to strip the tags before passing as a feedback key
      const formattedFeedback = feedback ? stripHtml(feedback).result : null
      return formattedFeedback ? translate(`feedback^${formattedFeedback}`) : null
    }
  }
  if(response) {
    return <p>{translatedCMSResponseFeedback(latestAttempt, question)}</p>
  }
}

function renderFeedback(feedback) {
  if (feedback && typeof feedback === 'string') {
    feedback = <p>{feedback}</p>
  }
  return feedback
}

const SpokenFeedback = ({ content, feedbackType, renderSection, }: { content: React.ReactNode, feedbackType: string, renderSection: (contentId?: string, rendered?: React.ReactNode) => JSX.Element }) => {
  const { contentId, readAloudButtonProps, rendered, } = useReadAloudSection({ content, })
  if (!readAloudButtonProps.text) { return renderSection() }

  return (
    <div className={`feedback-and-read-aloud ${getFeedbackState(feedbackType)}`}>
      {renderSection(contentId, rendered)}
      <ReadAloudButton key={`speaker-${readAloudButtonProps.text}`} label="Read the feedback aloud" {...readAloudButtonProps} />
    </div>
  )
}

function incorrectContinueFeedback(latestAttempt, correctResponse, unspokenClass?: string) {
  return (
    <div className="incorrect-continue">
      <p><b>Good try!</b> Compare your response to the strong response, and then go on to the next question.</p>
      <dl className="response-comparison">
        <dt className={unspokenClass}>Your response:</dt>
        <dd className={unspokenClass}>{parse(latestAttempt || '')}</dd>
        <dt>A strong response:</dt>
        <dd>{parse(correctResponse || '')}</dd>
      </dl>
    </div>
  )
}

const Feedback = ({
  correctResponse,
  feedbackType,
  feedbackText,
  feedbackHtml,
  language,
  latestAttempt,
  question,
  showTranslation,
  translate,
  translatedFeedbackText,
  languageLocale
}: FeedbackProps) => {

  function getTranslatedFeedback(showTranslation, question, feedbackType) {
    if(!showTranslation) { return null }

    const showInstructions = feedbackType === INSTRUCTIONS || feedbackType === GET_QUESTION_INSTRUCTIONS
    const showOverride = feedbackType === OVERRIDE || feedbackType === REVISE_UNMATCHED || feedbackType === CONTINUE
    const showCMSResponse = feedbackType === CORRECT_MATCHED || (feedbackType === REVISE_MATCHED && question?.question_type === CONNECT_FILL_IN_BLANKS)

    if (question?.translation?.instructions && showInstructions) {
      const { translation } = question
      const { instructions } = translation
      return renderFeedback(instructions)
    }
    if(feedbackType === DEFAULT_FILL_IN_BLANK) {
      const value = translate('feedback^Fill in the blank with the correct option.')
      return renderFeedback(value)
    }
    if(feedbackType === DEFAULT_WITH_CUES) {
      const value = question?.cues?.length === 1 ? translate('feedback^Combine the sentences into one sentence. Use the joining word.') : translate('feedback^Combine the sentences into one sentence. Use one of the joining words.')
      return renderFeedback(value)
    }
    if(feedbackType === DEFAULT) {
      const value = translate('feedback^Combine the sentences into one sentence. Use one of the joining words.')
      return renderFeedback(value)
    }
    if (feedbackText && showOverride) {
      const value = translate(`feedback^${feedbackText}`)
      return renderFeedback(value)
    }
    if (feedbackType === INCORRECT_CONTINUE) {
      const value = translatedIncorrectContinueFeedback(translate, latestAttempt, correctResponse)
      return renderFeedback(value)
    }
    if (feedbackType === REVISE_MATCHED) {
      const value = translatedReviseMatchedFeedback(question, latestAttempt, translate)
      return renderFeedback(value)
    }
    if (showCMSResponse) {
      const value = translatedCMSResponseFeedback(latestAttempt, question)
      return renderFeedback(value)
    }
  }

  function renderTranslatedFeedback() {
    const translatedFeedback = getTranslatedFeedback(showTranslation, question, feedbackType)
    if(!translatedFeedback) { return }
    return <div className="translated-feedback" lang={languageLocale}>{translatedFeedback}</div>
  }

  const { readAloudEnabled, } = useReadAloud()
  const unspokenClass = readAloudEnabled ? UNSPOKEN_CLASS : undefined

  function renderFeedbackContent() {
    if (feedbackType === INCORRECT_CONTINUE) {
      return incorrectContinueFeedback(latestAttempt, correctResponse, unspokenClass)
    }
    if (language) {
      const textClass = rightToLeftLanguages.includes(language) ? 'right-to-left' : ''
      const translationClass = unspokenClass ? `${textClass} ${unspokenClass}`.trim() : textClass
      const translationPresent = language !== ENGLISH
      return (
        <div>
          <p>{feedbackText}</p>
          {translationPresent && <br />}
          {translationPresent && translatedFeedbackText !== undefined && <p className={translationClass} lang={languageLocale}>{translatedFeedbackText}</p>}
        </div>
      )
    }
    if (feedbackHtml !== undefined) {
      if (feedbackType === REVISE_MATCHED || feedbackType === CORRECT_MATCHED) {
        return <span><p dangerouslySetInnerHTML={{ __html: feedbackHtml }} /></span>
      }
      return <p dangerouslySetInnerHTML={{ __html: feedbackHtml }} />
    }
    if (feedbackText !== undefined) {
      return <p>{feedbackText}</p>
    }
    return null
  }

  // Derives a stable text key from the feedback source strings so the
  // aria-live <section> remounts (and triggers a screen-reader
  // re-announcement) only when the underlying content actually changes.
  function feedbackKey() {
    if (feedbackType === INCORRECT_CONTINUE) {
      return `${latestAttempt || ''}${correctResponse || ''}`
    }
    return `${feedbackText || ''}${feedbackHtml || ''}${translatedFeedbackText || ''}`
  }

  const renderSection = (contentId?: string, rendered: React.ReactNode = renderFeedbackContent()) => (
    <section aria-atomic={true} aria-live="assertive" className={getCSSClasses(feedbackType)} key={feedbackKey()} role="status">
      <div className='feedback-row student-feedback-inner-container'>
        <img alt="" aria-hidden="true" className={getIconClassName(feedbackType)} src={getFeedbackIcon(feedbackType)} />
        <div id={contentId}>
          {rendered}
          {renderTranslatedFeedback()}
        </div>
      </div>
    </section>
  )

  if (!readAloudEnabled) { return renderSection() }

  return <SpokenFeedback content={renderFeedbackContent()} feedbackType={feedbackType} renderSection={renderSection} />
}

export { Feedback };
